import { ThemeName } from "./palette";
import {
  ResolverEnv,
  resolveSystem,
  subscribeSystem as realSubscribe,
  subscribeOfficeTheme as realSubscribeOffice,
  setTeamsTheme as storeTeamsTheme,
  getEnv,
} from "./officeThemeResolver";
import { ThemeIO, ThemePreference, readPreference, writePreference, readTeamsClientTheme, writeTeamsClientTheme, getIO, KEY } from "./persistence";
import { applyToDocument } from "./applyToDocument";

interface ControllerDeps {
  io: ThemeIO;
  env: ResolverEnv;
  subscribeSystem: (cb: () => void) => () => void;
  subscribeOfficeTheme: (cb: () => void) => (() => void) | null;
  apply: (t: ThemeName) => void;
}

// Host-context options: which storage key the preference lives under (so the
// Teams tab / browser embed / normal pages each keep their own choice) and
// what "nothing saved yet" resolves to (the embed defaults to light because
// it usually sits inside a light host website, not the user's OS chrome).
export interface ControllerOptions {
  storageKey?: string;
  defaultPreference?: ThemePreference;
}

let deps: ControllerDeps | null = null;
let storageKey: string = KEY;
let preference: ThemePreference = "system";
let effective: ThemeName = "light";
let started = false;
let officeSubscribed = false;
let officeCleanup: (() => void) | null = null;
const listeners = new Set<() => void>();

const compute = (): ThemeName => (preference === "system" ? resolveSystem(deps!.env) : preference);
const notify = () => listeners.forEach((l) => l());

const recompute = () => {
  const next = compute();
  if (next !== effective) { effective = next; deps!.apply(effective); notify(); }
};

export const themeController = {
  init(custom?: Partial<ControllerDeps> & ControllerOptions): void {
    if (started) return;                       // idempotent — one subscription
    deps = {
      io: custom?.io ?? getIO(),
      env: custom?.env ?? getEnv(),
      subscribeSystem: custom?.subscribeSystem ?? realSubscribe,
      subscribeOfficeTheme: custom?.subscribeOfficeTheme ?? realSubscribeOffice,
      apply: custom?.apply ?? applyToDocument,
    };
    storageKey = custom?.storageKey ?? KEY;
    preference = readPreference(deps.io, storageKey, custom?.defaultPreference ?? "system");
    effective = compute();
    deps.apply(effective);
    deps.subscribeSystem(() => recompute());
    started = true;
  },
  // Called after Office.onReady: the Office theme is only readable then, so we
  // register the Outlook OfficeThemeChanged handler (once) and re-resolve.
  refresh(): void {
    if (!started) return;
    if (!officeSubscribed) {
      const cleanup = deps!.subscribeOfficeTheme(() => recompute());
      if (cleanup) { officeSubscribed = true; officeCleanup = cleanup; }
    }
    recompute();
  },
  // Called by Teams-hosted pages: teams-js reports the client theme via
  // getContext / registerOnThemeChangeHandler. Store it and re-resolve so
  // "System" follows the Teams client rather than only prefers-color-scheme.
  setTeamsTheme(theme: string | undefined | null): void {
    // Cache the normalized value so the next load's pre-paint script and
    // seedTeamsThemeFromCache can follow the Teams client instead of the OS;
    // a null report invalidates the cache.
    writeTeamsClientTheme(storeTeamsTheme(theme));
    if (started) recompute();
  },
  // Called by Teams-hosted pages BEFORE init(): seeds the resolver with the
  // client theme cached on the previous visit, so the first resolve follows
  // Teams instead of the OS and doesn't flip once getContext reports in.
  seedTeamsThemeFromCache(): void {
    const cached = readTeamsClientTheme();
    if (cached) storeTeamsTheme(cached);
  },
  getPreference: () => preference,
  getEffective: () => effective,
  setPreference(p: ThemePreference): void {
    preference = p;
    writePreference(p, deps!.io, storageKey);
    const next = compute();
    effective = next;
    deps!.apply(effective);
    notify();
  },
  subscribe(l: () => void): () => void { listeners.add(l); return () => listeners.delete(l); },
  __resetForTests(): void {
    try { officeCleanup?.(); } catch { /* noop */ }
    deps = null; storageKey = KEY;
    preference = "system"; effective = "light"; started = false;
    officeSubscribed = false; officeCleanup = null; listeners.clear();
    storeTeamsTheme(null);
  },
};
